// ============================================================
// TERROR ENGINE — ORIENTATION LOCK
// ------------------------------------------------------------
// Universal portrait lock for the whole Illegal Empire. Two layers,
// belt-and-suspenders, because no single approach works on every
// device/runtime:
//
//   1. HARD LOCK (preferred) — the real Screen Orientation Lock API
//      (`screen.orientation.lock("portrait")`). This actually works in:
//        • an installed PWA / Android TWA (the Play Store build path)
//        • Chrome/Android in fullscreen
//      It silently no-ops in a normal mobile browser tab (the OS won't
//      let a plain tab seize orientation) — that's expected; layer 2
//      covers that case.
//
//   2. ROTATE GATE (fallback) — when the device reports landscape and
//      the hard lock didn't take, we drop a full-screen "rotate your
//      device" curtain that the player cannot move past until they turn
//      back to portrait. This is exactly the behaviour Terror shipped
//      before; it's kept as the universal safety net.
//
// ── WHY THIS LIVES IN THE ENGINE ────────────────────────────
// One source of truth for "are we portrait?" across Plaza, Pierre,
// Workshop, Dungeon, and every future room. No more per-file copies
// of `isPortrait()` drifting apart. The barrel (engine/index.js)
// already promises `isPortrait` — this is now its real home.
//
// ── USAGE ───────────────────────────────────────────────────
//   import { useOrientationLock, RotateGate } from "./engineBarrel";
//
//   function Room() {
//     const { needsRotate } = useOrientationLock();   // call once near root
//     return (
//       <>
//         ...your room...
//         <RotateGate show={needsRotate} />            // render last (top z)
//       </>
//     );
//   }
//
// `RotateGate` self-shows from the same hook internally too, so you can
// also just drop `<RotateGate />` with no props and it manages itself.
// ============================================================

import { useState, useEffect, useCallback } from "react";

// ── Device + orientation primitives (the ONE true source) ─────
export const isMobile = () =>
  (typeof window !== "undefined") &&
  (("ontouchstart" in window) || navigator.maxTouchPoints > 0);

// Portrait = tall viewport on a touch device. Desktops are never
// "portrait" for layout purposes even if the window is tall.
export const isPortrait = () =>
  isMobile() &&
  (typeof window !== "undefined") &&
  window.innerHeight >= window.innerWidth;

// Landscape on a phone = the state we want to block / correct.
export const isLandscapeMobile = () =>
  isMobile() &&
  (typeof window !== "undefined") &&
  window.innerWidth > window.innerHeight;

// ── Attempt the real hard lock. Returns a promise<boolean>. ───
// Never throws — a rejected lock just resolves false so callers can
// fall through to the rotate gate.
export async function lockPortrait() {
  try {
    const so = (typeof window !== "undefined") && window.screen && window.screen.orientation;
    if (so && typeof so.lock === "function") {
      await so.lock("portrait");
      return true;
    }
  } catch (_) {
    // Most common: "lock() must be called from fullscreen / installed app".
    // Expected in a normal browser tab — not an error worth surfacing.
  }
  return false;
}

// ── useOrientationLock ────────────────────────────────────────
// Call once near a screen root. Tries the hard lock on mount, then
// tracks orientation so the rotate gate can show whenever the device
// is in landscape and the hard lock didn't take.
//
// Returns:
//   needsRotate  boolean  — true when we should show the RotateGate
//   portrait     boolean  — convenience live portrait flag
//   hardLocked   boolean  — true if the OS-level lock succeeded
export function useOrientationLock() {
  const [portrait, setPortrait]     = useState(isPortrait);
  const [hardLocked, setHardLocked] = useState(false);

  // Try the OS-level lock once on mount (and after any user gesture
  // re-grants fullscreen, browsers may allow a retry).
  useEffect(() => {
    let alive = true;
    lockPortrait().then(ok => { if (alive) setHardLocked(ok); });
    return () => { alive = false; };
  }, []);

  // Keep the live portrait flag in sync with the device.
  useEffect(() => {
    const update = () => setPortrait(isPortrait());
    update();
    window.addEventListener("resize", update);
    window.addEventListener("orientationchange", update);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("orientationchange", update);
    };
  }, []);

  // Gate shows only on a phone that is currently landscape. Desktops,
  // tablets-as-desktop, and portrait phones never see it. If the hard
  // lock took, the device should never report landscape anyway — but if
  // it somehow does we still correct with the gate.
  const needsRotate = isLandscapeMobile();

  return { needsRotate, portrait, hardLocked };
}

// ── usePortrait ───────────────────────────────────────────────
// Lightweight subscriber for components that only need the live
// portrait flag (e.g. PortraitFrame) without driving the lock.
export function usePortrait() {
  const [portrait, setPortrait] = useState(isPortrait);
  useEffect(() => {
    const update = () => setPortrait(isPortrait());
    update();
    window.addEventListener("resize", update);
    window.addEventListener("orientationchange", update);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("orientationchange", update);
    };
  }, []);
  return portrait;
}

// ── RotateGate ────────────────────────────────────────────────
// Full-screen curtain shown when a phone is held in landscape. The
// player cannot interact with anything beneath it. Self-manages its
// own visibility if `show` is omitted, so a bare <RotateGate /> works.
export function RotateGate({ show = null }) {
  // Self-managed mode when no `show` prop is passed.
  const [selfLandscape, setSelfLandscape] = useState(isLandscapeMobile);
  useEffect(() => {
    if (show !== null) return; // controlled — don't self-manage
    const update = () => setSelfLandscape(isLandscapeMobile());
    update();
    window.addEventListener("resize", update);
    window.addEventListener("orientationchange", update);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("orientationchange", update);
    };
  }, [show]);

  // Let a tap try the hard lock again — a user gesture sometimes
  // unlocks the permission the automatic attempt couldn't get.
  const retry = useCallback(() => { lockPortrait(); }, []);

  const visible = show !== null ? show : selfLandscape;
  if (!visible) return null;

  return (
    <div
      onClick={retry}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 99999, // above absolutely everything
        background: "radial-gradient(circle at 50% 40%, #14100a 0%, #050308 70%)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 26,
        textAlign: "center",
        fontFamily: "'Cinzel', 'Palatino Linotype', serif",
        color: "#e8d5a3",
        userSelect: "none",
        WebkitUserSelect: "none",
        cursor: "pointer",
        padding: 24,
      }}
    >
      <style>{`
        @keyframes ttcRotateSpin {
          0%   { transform: rotate(0deg);   }
          55%  { transform: rotate(-90deg); }
          100% { transform: rotate(-90deg); }
        }
        @keyframes ttcRotateGlow {
          0%,100% { opacity: 0.55; }
          50%     { opacity: 1;    }
        }
      `}</style>

      {/* Rotating phone glyph */}
      <div style={{
        width: 92, height: 92,
        animation: "ttcRotateSpin 2.4s ease-in-out infinite",
        filter: "drop-shadow(0 0 14px #c8963c66)",
      }}>
        <svg viewBox="0 0 64 64" width="92" height="92" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <rect x="20" y="6" width="24" height="52" rx="5"
            fill="none" stroke="#c8963c" strokeWidth="3" />
          <rect x="26" y="13" width="12" height="34" rx="1.5"
            fill="#c8963c" opacity="0.18" />
          <circle cx="32" cy="52" r="2.4" fill="#c8963c" />
        </svg>
      </div>

      <div style={{
        fontSize: 19,
        letterSpacing: 3,
        fontWeight: 700,
        color: "#e8b84b",
        textShadow: "0 0 16px #c8963c55",
      }}>
        ROTATE YOUR DEVICE
      </div>

      <div style={{
        fontSize: 13,
        letterSpacing: 1.5,
        color: "#b89a5a",
        lineHeight: 1.7,
        maxWidth: 280,
        animation: "ttcRotateGlow 2.2s ease-in-out infinite",
      }}>
        The Illegal Empire is played in portrait.
        <br />
        Turn your phone upright to continue.
      </div>

      <div style={{ fontSize: 10, letterSpacing: 2, color: "#7a6040", marginTop: 4 }}>
        ◆
      </div>
    </div>
  );
}

export default RotateGate;
